/**
 * Image Uploader Component
 * 
 * Drag & drop image uploader with preview and Supabase Storage integration
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { uploadBlogImage, resizeImage } from '../../utils/supabaseStorage';

/**
 * Image Uploader Component
 * @param {Object} props
 * @param {string} props.value - Current image URL
 * @param {Function} props.onChange - Callback when image changes (url) => void
 * @param {string} props.blogId - Blog ID for organizing files
 * @param {string} props.userId - User ID for organizing files
 * @param {string} props.label - Label for the uploader
 */
export default function ImageUploader({
  value = '',
  onChange,
  blogId = 'temp',
  userId,
  label = 'Header Image',
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(value);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const isFileDialogOpenRef = useRef(false);

  // Update preview when value changes externally
  useEffect(() => {
    if (value) {
      setPreview(value);
    }
  }, [value]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFile = useCallback(async (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('File size exceeds 5MB limit. Please upload a smaller image.');
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result);
      };
      reader.readAsDataURL(file);

      // Resize image before upload (optional optimization)
      const resizedFile = await resizeImage(file, 1920, 1080, 0.8);

      // Upload to Supabase Storage
      const result = await uploadBlogImage(resizedFile, blogId, userId);

      if (result.success) {
        setPreview(result.url);
        onChange(result.url);
      } else {
        setError(result.error || 'Failed to upload image');
        setPreview('');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image');
      setPreview('');
    } finally {
      setIsUploading(false);
    }
  }, [blogId, userId, onChange]);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        await handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleFileSelect = useCallback(
    async (e) => {
      // Mark that file dialog is closing
      isFileDialogOpenRef.current = false;
      
      const files = e.target.files;
      if (files && files.length > 0) {
        // Keep focus check disabled during file processing
        window.__adminFocusCheckDisabled = true;
        try {
          await handleFile(files[0]);
        } finally {
          // Re-enable after file processing completes
          setTimeout(() => {
            window.__adminFocusCheckDisabled = false;
          }, 500);
        }
      } else {
        // No file selected, re-enable immediately
        setTimeout(() => {
          window.__adminFocusCheckDisabled = false;
        }, 300);
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFile]
  );

  const handleFileInputClick = useCallback((e) => {
    // Prevent event from bubbling up
    e.stopPropagation();
    // Mark that file dialog is opening
    isFileDialogOpenRef.current = true;
    // Temporarily disable focus-based admin verification
    window.__adminFocusCheckDisabled = true;
    // Open file dialog
    fileInputRef.current?.click();
  }, []);

  const handleRemove = () => {
    setPreview('');
    onChange('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {preview ? (
        <div className="relative">
          <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-300 bg-gray-100">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Click the area below to change the image
          </p>
        </div>
      ) : (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${
              isDragging
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }
            ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
          `}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleFileInputClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            onClick={(e) => {
              // Prevent click from bubbling
              e.stopPropagation();
            }}
            onFocus={(e) => {
              // Prevent focus events from triggering admin verification
              e.stopPropagation();
            }}
            onBlur={(e) => {
              // Re-enable admin focus check after a short delay
              // This gives time for the file dialog to close and focus to return
              setTimeout(() => {
                window.__adminFocusCheckDisabled = false;
                isFileDialogOpenRef.current = false;
              }, 500);
            }}
            className="hidden"
            aria-label="Upload image"
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              <p className="text-sm text-gray-600">Uploading image...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                {isDragging ? (
                  <Upload className="w-6 h-6 text-primary-600" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, WebP, GIF up to 5MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}

      {!preview && !isUploading && (
        <p className="mt-2 text-xs text-gray-500">
          Optional: Add a header image to make your blog post more engaging
        </p>
      )}
    </div>
  );
}
